import { useState, useCallback, useEffect, useRef } from 'react'

export interface UseExpandedGroupsProps {
  data: any[]
  groupByFields: string[]
  autoExpandSingleChild?: boolean
  defaultExpandAll?: boolean
}

export function useExpandedGroups({
  data,
  groupByFields,
  autoExpandSingleChild = false,
  defaultExpandAll = false
}: UseExpandedGroupsProps) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  )
  const initializedRef = useRef(false)
  const dataRef = useRef<any[]>([])

  // Helper function: Find groups with only one child
  const findSingleChildGroups = useCallback(
    (
      items: any[],
      fields: string[],
      level = 0,
      parentPath: string[] = []
    ): string[] => {
      if (level >= fields.length) return []

      const field = fields[level]
      const groups: Record<string, any> = {}
      const singleChildGroupIds: string[] = []

      // Create groups
      items.forEach(item => {
        const groupValue = item[field]?.toString() || 'N/A'
        const groupKey =
          [...parentPath, groupValue].join('-') ||
          `empty-${Math.random().toString(36).substring(2)}`

        if (!groups[groupKey]) {
          groups[groupKey] = {
            id: `group-${groupKey}`,
            children: [],
            childCount: 0
          }
        }

        groups[groupKey].childCount++
        groups[groupKey].children.push({ ...item })
      })

      // Check each group
      Object.entries(groups).forEach(([key, group]) => {
        // If group has only one child, add to results
        if (group.childCount === 1) {
          singleChildGroupIds.push(group.id)
        }

        // Process next level groups recursively
        if (level < fields.length - 1) {
          const childGroups = findSingleChildGroups(
            group.children,
            fields,
            level + 1,
            [...parentPath, key]
          )
          singleChildGroupIds.push(...childGroups)
        }
      })

      return singleChildGroupIds
    },
    []
  )

  // Helper function: Find all group IDs
  const findAllGroupIds = useCallback(
    (
      items: any[],
      fields: string[],
      level = 0,
      parentPath: string[] = []
    ): string[] => {
      if (level >= fields.length) return []

      const field = fields[level]
      const groups: Record<string, any> = {}
      const allGroupIds: string[] = []

      // Create groups
      items.forEach(item => {
        const groupValue = item[field]?.toString() || 'N/A'
        const groupKey =
          [...parentPath, groupValue].join('-') ||
          `empty-${Math.random().toString(36).substring(2)}`

        if (!groups[groupKey]) {
          groups[groupKey] = {
            id: `group-${groupKey}`,
            children: [],
            childCount: 0
          }
        }
        groups[groupKey].children.push({ ...item })
      })

      // Collect all group IDs and recurse
      Object.entries(groups).forEach(([key, group]) => {
        allGroupIds.push(group.id)
        if (level < fields.length - 1) {
          const childGroups = findAllGroupIds(
            group.children,
            fields,
            level + 1,
            [...parentPath, key]
          )
          allGroupIds.push(...childGroups)
        }
      })

      return allGroupIds
    },
    []
  )

  // Handle default and auto-expansion
  useEffect(() => {
    if (
      data.length === 0 ||
      (initializedRef.current && dataRef.current === data)
    ) {
      return
    }

    dataRef.current = data

    let groupIdsToExpand: string[] = []

    if (defaultExpandAll) {
      groupIdsToExpand = findAllGroupIds(data, groupByFields)
    } else if (autoExpandSingleChild) {
      groupIdsToExpand = findSingleChildGroups(data, groupByFields)
    }

    if (groupIdsToExpand.length > 0) {
      const newExpandedGroups: Record<string, boolean> = {}
      groupIdsToExpand.forEach(groupId => {
        newExpandedGroups[groupId] = true
      })
      setExpandedGroups(newExpandedGroups)
    }

    initializedRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    defaultExpandAll,
    autoExpandSingleChild,
    findAllGroupIds,
    findSingleChildGroups
  ])

  // Toggle group expand state
  const handleToggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }))
  }, [])

  return { expandedGroups, handleToggleGroup }
}
